// Shell: sidebar oscuro colapsable + topbar blanca con hairline inferior.
// Colapsado muestra SOLO el simbolo y los iconos, centrados: nada de texto recortado.
// Escape colapsa el sidebar solo si no hay ninguna capa abierta encima.
//
// HU-32a (MG-ILE-63 · 32a-6): el menu y la cabecera salen de `GET /sesion`. El menu pinta
// `modulos_permitidos` (la union de los modulos de los roles de la persona) y la cabecera
// `texto_de_la_cabecera`. La lista por rol que tenia el frontend (`PERMISOS`) ya no existe (§8.1 R4).
//
// MG-ILE-63 («Vacío 2», 2-oct-2026): la cabecera ya no pinta un chip por rol. Con ocho roles
// crecia a tres filas y cortaba el nombre del sistema. Los roles con su ambito, «Usuario externo»
// y «Cerrar sesión» viven en el panel del avatar (`MenuDeSesion`).
import { useEffect, useRef, useState } from 'react';
import {
  LayoutDashboard, Inbox, FilePlus2, Stethoscope, FileCheck2, Send, ListChecks,
  ChartLine, Settings, LogOut, Menu, Bell, Search, RotateCcw,
} from 'lucide-react';
import { Logotipo, Isotipo } from './BrandMark';
import MenuDeSesion from './MenuDeSesion';
import { MODULOS, modulosDelMenu } from '../data/navegacion';
import { Modal, Button, hayModalAbierto, EstadoConexion } from './ui';
import type { SesionDelServidor } from '../api/sesion';
import type { ReactNode } from 'react';
import type { Icono } from './ui';
import type { ModuloDeNavegacion } from '../data/navegacion';
import type { UsuarioSimulado } from '../lib/sesion';

// El mapa se indexa con `MODULOS[k].icon`, que es texto: se declara su forma para que
// buscar un icono que no existe devuelva `undefined` y no un `any` silencioso.
const ICONS: Record<string, Icono> = {
  LayoutDashboard, Inbox, FilePlus2, Stethoscope, FileCheck2, Send, ListChecks, ChartLine, Settings,
};

// El simbolo ⌘ es de Mac. En Windows y Linux el atajo real es Ctrl: mostrar ⌘ ahi confunde.
// `userAgentData` es reciente y TypeScript todavia no la declara en `Navigator`. Se
// consulta con una afirmacion acotada, y no apagando la comprobacion del archivo.
const plataforma = typeof navigator !== 'undefined'
  ? ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
     || navigator.platform || navigator.userAgent)
  : '';
const esMac = /Mac|iPhone|iPad|iPod/.test(plataforma);
const TECLAS = esMac ? ['⌘', 'K'] : ['Ctrl', 'K'];

function Atajo() {
  return (
    <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none select-none">
      {TECLAS.map((t) => (
        <kbd key={t}
          className="min-w-5 h-6 px-1.5 inline-flex items-center justify-center rounded-[5px]
                     bg-white border border-hairline border-b-2 border-b-[#D3DFE5]
                     font-sans text-micro font-semibold leading-none text-ink-soft">
          {t}
        </kbd>
      ))}
    </span>
  );
}

// LO QUE TODAVÍA NO EXISTE NO SE MUESTRA (QA, 14-sep-2026). El buscador global, el
// reinicio de la demostración, la campana de notificaciones y el «Conectado / Offline»
// venían del prototipo y no hacían nada: el buscador no buscaba, el reinicio llamaba a
// una función vacía, la campana marcaba 0 siempre. Se dejan tras esta constante para
// volver a encenderlos cuando tengan servicio detrás.
export const MOSTRAR_HERRAMIENTAS_DEL_PROTOTIPO = false;

const BUSCADOR: Record<string, string> = {
  registrador: 'Buscar mis casos, productos, lotes...',
  comite: 'Buscar casos por evaluar, causalidad...',
  asuntos_regulatorios: 'Buscar informes, notificaciones, autoridades...',
  aprobador: 'Buscar informes pendientes de aprobación...',
  representante_pais: 'Buscar casos de mi país, cargos...',
  administrador: 'Buscar usuarios, catálogos, reglas...',
  especialista: 'Buscar entre los casos a los que te invitaron...',
};

// Marca. Colapsada = solo el isotipo, y al pulsarla se expande.
// El logotipo va en su version negativa porque se apoya en el azul corporativo (manual,
// pag. 10). Ni la palabra "ilender" ni su tipografia se recomponen aqui: entran dibujadas
// dentro del archivo oficial. "Farmacovigilancia" es el nombre del sistema, no parte de
// la marca, por eso va debajo y separado.
function MarcaDelSidebar({ colapsado, onExpandir }: { colapsado: boolean; onExpandir: () => void }) {
  return (
    <div className={'h-16 flex items-center border-b border-white/10 ' + (colapsado ? 'justify-center px-0' : 'px-5')}>
      <button type="button" onClick={() => colapsado && onExpandir()}
        title={colapsado ? 'Expandir el menú' : undefined}
        aria-label={colapsado ? 'Expandir el menú' : 'ilender Farmacovigilancia'}
        className={'flex items-center rounded-xl transition-all ' +
          (colapsado ? 'p-2 hover:bg-white/10 cursor-pointer' : 'cursor-default')}>
        {colapsado ? (
          <Isotipo sobreFondoOscuro alto={30} />
        ) : (
          <span className="block text-left">
            <Logotipo sobreFondoOscuro alto={26} conAire={false} />
            <span className="block text-meta font-medium text-cian-suave mt-1.5">
              Farmacovigilancia
            </span>
          </span>
        )}
      </button>
    </div>
  );
}

type PropsDeAppShell = {
  usuario: UsuarioSimulado;
  /** La sesion del servidor: menu y cabecera. */
  sesion: SesionDelServidor;
  /** La entrada del menu que va resaltada. `undefined` en las vistas internas. */
  modulo?: ModuloDeNavegacion;
  onModulo: (modulo: ModuloDeNavegacion) => void;
  onLogout: () => void;
  onReset?: () => void;
  alertas?: number;
  children?: ReactNode;
};

/**
 * PANTALLA ESTRECHA = MENU COLAPSADO (MG-ILE-710, 2-oct-2026; lineamiento, Pendientes 10).
 *
 * Por debajo de `md` (768 px de ancho CSS) el menu expandido se comia 248 de los 390 px de un
 * telefono, y a 320 px (WCAG 1.4.10) dejaba 72 px para la pantalla: el texto se partia letra a
 * letra. Lo mismo pasa al ampliar al 200 % en un portatil de 1366 (quedan 683 px CSS). Ahora el
 * menu NACE colapsado en una ventana estrecha y se colapsa solo al estrecharse (y se expande al
 * volver a ensancharse); el boton de la cabecera lo sigue abriendo cuando la persona lo pide.
 */
const VENTANA_ESTRECHA = '(max-width: 767.98px)';

// Guarda completa y no `window.matchMedia?.(...)`: en jsdom `matchMedia` no existe.
const esVentanaEstrecha = (): boolean => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function' && window.matchMedia(VENTANA_ESTRECHA).matches;

export default function AppShell({ usuario, sesion, modulo, onModulo, onLogout, onReset, alertas = 0, children }: PropsDeAppShell) {
  const [colapsado, setColapsado] = useState(esVentanaEstrecha);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const buscador = useRef<HTMLInputElement>(null);
  const permitidos = modulosDelMenu(sesion.modulos_permitidos);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia(VENTANA_ESTRECHA);
    const alCambiar = (e: MediaQueryListEvent) => setColapsado(e.matches);
    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmarSalida && !hayModalAbierto()) setColapsado((v) => !v);
      if (MOSTRAR_HERRAMIENTAS_DEL_PROTOTIPO && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); buscador.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmarSalida]);

  return (
    <div className="flex h-screen overflow-hidden bg-canvas">
      {/* ---------- Sidebar ---------- */}
      {/* `superficie-oscura` (HU-51): sobre este azul el contorno de foco pasa a blanco. */}
      <aside className={'superficie-oscura bg-marca-profundo flex flex-col shrink-0 transition-[width] duration-200 ease-out ' +
        (colapsado ? 'w-[4.75rem]' : 'w-[16.5rem]')}>

        <MarcaDelSidebar colapsado={colapsado} onExpandir={() => setColapsado(false)} />

        {/* Navegacion */}
        <nav className={'flex-1 py-3 overflow-y-auto overflow-x-hidden ' + (colapsado ? 'px-2.5' : 'px-2')}
          aria-label="Navegación principal">
          {permitidos.map((k: ModuloDeNavegacion) => {
            const m = MODULOS[k];
            const Icon = ICONS[m.icon];
            const activo = modulo === k;
            // Un modulo SIN pantalla todavia se lista sin etiqueta (decision del operador,
            // 5-oct-2026: el chip «Sprint 3» no le decia nada al usuario); `aria-disabled` lo
            // dice al lector. Sigue siendo pulsable a proposito, porque lo que abre es la
            // explicacion de que puede hacer ese rol hoy.
            const disponible = m.disponible !== false;
            return (
              <button type="button" key={k} onClick={() => onModulo(k)} data-testid={`menu-${k}`}
                title={colapsado ? m.label : undefined}
                aria-current={activo ? 'page' : undefined}
                aria-disabled={disponible ? undefined : true}
                className={'w-full flex items-center rounded-xl mb-0.5 text-compacto font-medium leading-5 text-left transition-colors relative ' +
                  (colapsado ? 'justify-center h-11' : 'gap-3 px-3 py-2 min-h-10') +
                  // Sin opacidad en los modulos sin pantalla (MG-ILE-710; par 9 del lineamiento): al
                  // 60 % el texto bajaba a 3,25:1, y siguen siendo pulsables, no inactivos.
                  (activo ? ' bg-white/12 text-white font-semibold' : ' text-cian-suave hover:bg-white/6 hover:text-white')}>
                {activo && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r bg-cian" />}
                <Icon size={17} strokeWidth={2} className="shrink-0" />
                {/* Envuelve en vez de cortarse con «…» (WCAG 1.4.12 y zoom): el rotulo es lo
                    unico que dice a donde lleva el boton. */}
                {!colapsado && <span className="min-w-0 break-words">{m.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Pie del sidebar */}
        <div className={'border-t border-white/10 py-3 ' + (colapsado ? 'px-2.5' : 'px-2')}>
          <button type="button" onClick={() => setConfirmarSalida(true)} title={colapsado ? 'Cerrar sesión' : undefined}
            className={'w-full flex items-center rounded-xl text-compacto font-medium leading-5 text-cian-suave hover:bg-white/6 hover:text-white transition-colors ' +
              (colapsado ? 'justify-center h-11' : 'gap-3 px-3 py-2 min-h-10')}>
            <LogOut size={17} strokeWidth={2} className="shrink-0" />
            {!colapsado && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* ---------- Contenido ---------- */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* `min-h` y no `h`: con dos roles, o con el interlineado de WCAG 1.4.12, la cabecera
            crece en vez de montarse sobre la pantalla. */}
        <header className="min-h-16 py-2 bg-white border-b border-hairline flex items-center gap-3 px-4 sm:px-6 shrink-0">
          <button type="button" onClick={() => setColapsado(!colapsado)}
            aria-label={colapsado ? 'Expandir el menú' : 'Colapsar el menú'}
            aria-expanded={!colapsado}
            className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl border border-control-borde hover:border-control-borde-fuerte text-ink-soft hover:text-ink hover:bg-canvas transition-colors">
            <Menu size={18} />
          </button>

          {MOSTRAR_HERRAMIENTAS_DEL_PROTOTIPO ? (
            <div className="flex-1 max-w-md relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-placeholder pointer-events-none" />
              <input ref={buscador} placeholder={BUSCADOR[usuario.rol]} aria-label="Buscar"
                aria-keyshortcuts={esMac ? 'Meta+K' : 'Control+K'}
                className={'w-full h-10 bg-canvas border border-hairline rounded-xl pl-10 text-body text-ink placeholder-placeholder focus:outline-none focus:border-marca focus:ring-4 focus:ring-marca-bruma transition-all ' +
                  (esMac ? 'pr-16' : 'pr-[5.375rem]')} />
              <Atajo />
            </div>
          ) : (
            // Sin buscador, el nombre del sistema ocupa su sitio: la cabecera no queda hueca.
            // Nunca se recorta (MG-ILE-63): desde 1024 px cede espacio el bloque de sesion. Por
            // debajo de 1024 px se oculta en vez de truncarse: a 320 px, al 200 % y con el
            // espaciado de 1.4.12 solo cabia «Farm…» (WCAG 1.4.4, 1.4.10 y 1.4.12; informe de
            // legibilidad MG-ILE-710, hallazgo 1). El sistema se sigue nombrando en el titulo de
            // la pestana y, con la barra lateral expandida, bajo el logotipo.
            <p className="text-titulo font-semibold text-ink whitespace-nowrap shrink-0 max-lg:hidden">Farmacovigilancia</p>
          )}

          {MOSTRAR_HERRAMIENTAS_DEL_PROTOTIPO && (
            <>
              {/* Con el buscador encendido, las herramientas van a la derecha, antes de la sesion. */}
              <div className="flex-1" />
              <div className="max-lg:hidden"><EstadoConexion /></div>

              <button type="button" onClick={onReset} title="Reiniciar los datos de la demostración"
                aria-label="Reiniciar los datos de la demostración"
                className="w-10 h-10 flex items-center justify-center rounded-xl text-ink-soft hover:text-ink hover:bg-canvas transition-colors">
                <RotateCcw size={17} />
              </button>

              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-xl text-ink-soft hover:text-ink hover:bg-canvas transition-colors relative"
                aria-label={`Notificaciones: ${alertas} pendientes`}>
                <Bell size={17} />
                {alertas > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-5 h-5 px-1 rounded-full bg-rose-700 text-white text-micro font-semibold tabular-nums flex items-center justify-center">
                    {alertas}
                  </span>
                )}
              </button>

              <span className="w-px h-8 bg-hairline mx-1" />
            </>
          )}

          {/* Una sola linea: «Nombre · rol principal», «+N roles» y el avatar, que abre los roles
              con su ambito y «Cerrar sesión» (MG-ILE-63). */}
          <MenuDeSesion sesion={sesion} nombreDeReserva={usuario.nombre} inicialDeReserva={usuario.inicial}
            onCerrarSesion={() => setConfirmarSalida(true)} />
        </header>

        {/* `scroll-py-24` (WCAG 2.4.11, MG-ILE-710): al tabular, el navegador desplaza el control
            enfocado hasta el borde de la vista, justo debajo de la barra fija de la evaluación
            (`sticky bottom-0`) o de la cabecera fija del instructivo (`sticky top-0`), que lo
            tapaban. Con este margen de desplazamiento el foco queda a la vista. */}
        <main className="flex-1 overflow-y-auto p-6 max-md:p-4 scroll-py-24">
          <div className="max-w-[87.5rem] mx-auto">{children}</div>
        </main>
      </div>

      <Modal open={confirmarSalida} onClose={() => setConfirmarSalida(false)}
        eyebrow="Sesión · Cerrar sesión" title="Cerrar sesión activa" icon={LogOut} tone="warning"
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmarSalida(false)}>Cancelar</Button>
          <Button variant="danger" onClick={onLogout}>Cerrar sesión</Button>
        </>}>
        <p>Se cerrará la sesión de <b>{sesion.usuario || usuario.nombre}</b>. Los casos guardados se conservan.</p>
      </Modal>
    </div>
  );
}
